/**
 * CreditsTab — beta publish entitlement management for one user.
 *
 * Reads the entitlement summary (credits / status / source / published count)
 * and the consumption history from the entitlement endpoints, and exposes
 * Grant / Revoke actions. All credit math lives in the backend provider; this
 * tab only reads the view model + triggers mutations, so a backend provider
 * change needs no change here.
 */
import { useState } from 'react';
import { useUnfreezeWallet, useUserEntitlement, useUserPublishHistory, useUserWallet } from '../hooks';
import { TableSkeleton } from '../components/LoadingSkeleton';
import { ErrorState } from '../components/EmptyState';
import { PublishHistoryTable } from '../components/PublishHistoryTable';
import { GrantRevokeCreditsModal } from '../components/GrantRevokeCreditsModal';
import { WalletAdjustModal } from '../components/WalletAdjustModal';
import type { EntitlementSource } from '../types';

const SOURCE_LABEL: Record<EntitlementSource, string> = {
  BETA_CREDIT: 'Beta Credit',
  ADMIN_GRANT: 'Beta Credit',
  ENTERPRISE: 'Enterprise',
  PURCHASE: 'Purchased',
};

export function CreditsTab({ userId }: { userId: string }) {
  const ent = useUserEntitlement(userId);
  const history = useUserPublishHistory(userId);
  const wallet = useUserWallet(userId);
  const [modal, setModal] = useState<'grant' | 'revoke' | null>(null);
  const [walletModal, setWalletModal] = useState<'grant' | 'revoke' | null>(null);
  const unfreeze = useUnfreezeWallet(userId);
  const [releaseReason, setReleaseReason] = useState('');

  if (ent.isLoading) return <TableSkeleton rows={3} cols={2} />;
  if (ent.isError || !ent.data)
    return (
      <ErrorState
        message={(ent.error as Error)?.message ?? 'Failed to load entitlement'}
        onRetry={() => void ent.refetch()}
      />
    );

  const e = ent.data;
  const hasRupeeWallet = Boolean(wallet.data && (wallet.data.balance > 0 || (wallet.data.frozen?.length ?? 0) > 0));

  return (
    <div className="space-y-5">
      {/* Summary */}
      <div className="space-y-2">
        <h3 className="text-[10px] uppercase tracking-widest font-bold text-on-surface-variant">
          {hasRupeeWallet ? 'Legacy Beta Publish Pool' : 'Publish Entitlement'}
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Field label={hasRupeeWallet ? 'Beta credits (Legacy)' : 'Remaining credits'} value={String(e.remainingCredits)} />
          <Field
            label={hasRupeeWallet ? 'Beta publish rights' : 'Publish rights'}
            value={e.canPublish ? 'Can Publish' : (hasRupeeWallet ? 'Wallet-only' : 'No Credits')}
            tone={e.canPublish ? 'ok' : 'warn'}
          />
          <Field label="Source" value={SOURCE_LABEL[e.entitlementSource] ?? 'Beta Credit'} />
          <Field label="Published" value={`${e.totalPublished} websites`} />
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => setModal('grant')}
          className="px-4 py-2 rounded-lg text-[10px] font-bold uppercase tracking-widest text-white bg-electric-violet hover:bg-electric-violet/90"
        >
          Grant credits
        </button>
        <button
          type="button"
          onClick={() => setModal('revoke')}
          disabled={e.remainingCredits === 0}
          className="px-4 py-2 rounded-lg text-[10px] font-bold uppercase tracking-widest border border-red-400/40 text-red-300 hover:bg-red-500/10 disabled:opacity-50"
        >
          Revoke credits
        </button>
      </div>

      {/* Rupee wallet (partner packs; 1 credit = ₹1) — support corrections */}
      <div className="space-y-2" data-testid="wallet-section">
        <h3 className="text-[10px] uppercase tracking-widest font-bold text-on-surface-variant">Rupee wallet</h3>
        {wallet.isLoading ? (
          <TableSkeleton rows={1} cols={2} />
        ) : wallet.isError || !wallet.data ? (
          <ErrorState message={(wallet.error as Error)?.message ?? 'Failed to load wallet'} onRetry={() => void wallet.refetch()} />
        ) : (
          <>
            {(wallet.data.frozen?.length ?? 0) > 0 && (
              <div className="rounded-lg border border-amber-400/40 bg-amber-500/10 p-3 space-y-2" data-testid="wallet-frozen">
                <p className="text-xs font-bold text-amber-200">Wallet on hold — the partner cannot spend credits (bank dispute).</p>
                <ul className="text-[11px] text-on-surface-variant list-disc pl-4">
                  {wallet.data.frozen!.map((f) => (
                    <li key={f.id}>{f.reason} (since {new Date(f.since).toLocaleString('en-IN')})</li>
                  ))}
                </ul>
                <div className="flex gap-2">
                  <input
                    value={releaseReason}
                    onChange={(ev) => setReleaseReason(ev.target.value)}
                    placeholder="Why the hold can be released (min 10 chars, audited)"
                    className="flex-1 px-3 py-2 rounded-lg bg-surface-container-lowest border border-border-subtle text-xs"
                  />
                  <button
                    type="button"
                    disabled={releaseReason.trim().length < 10 || unfreeze.isPending}
                    onClick={() => void unfreeze.mutateAsync({ reason: releaseReason.trim() }).then(() => setReleaseReason(''))}
                    className="px-4 py-2 rounded-lg text-[10px] font-bold uppercase tracking-widest border border-amber-400/40 text-amber-200 hover:bg-amber-500/10 disabled:opacity-50"
                  >
                    Release hold
                  </button>
                </div>
                {unfreeze.error && <p className="text-xs text-red-300">{(unfreeze.error as Error).message}</p>}
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <Field label="Wallet balance" value={`₹${wallet.data.balance.toLocaleString('en-IN')}`} />
              <Field
                label="Blended rate"
                value={wallet.data.blendedDiscountPercent === null ? '—' : `${wallet.data.blendedDiscountPercent}% off`}
              />
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setWalletModal('grant')}
                className="px-4 py-2 rounded-lg text-[10px] font-bold uppercase tracking-widest text-white bg-electric-violet hover:bg-electric-violet/90"
              >
                Add to wallet
              </button>
              <button
                type="button"
                onClick={() => setWalletModal('revoke')}
                disabled={wallet.data.balance === 0}
                className="px-4 py-2 rounded-lg text-[10px] font-bold uppercase tracking-widest border border-red-400/40 text-red-300 hover:bg-red-500/10 disabled:opacity-50"
              >
                Remove from wallet
              </button>
            </div>
            <WalletAdjustModal
              open={walletModal !== null}
              mode={walletModal ?? 'grant'}
              userId={userId}
              balance={wallet.data.balance}
              onClose={() => setWalletModal(null)}
            />
          </>
        )}
      </div>

      {/* History */}
      <div className="space-y-2">
        <h3 className="text-[10px] uppercase tracking-widest font-bold text-on-surface-variant">
          Publish history
        </h3>
        {history.isLoading ? (
          <TableSkeleton rows={3} cols={3} />
        ) : history.isError ? (
          <ErrorState
            message={(history.error as Error)?.message ?? 'Failed to load history'}
            onRetry={() => void history.refetch()}
          />
        ) : (
          <PublishHistoryTable rows={history.data ?? []} />
        )}
      </div>

      <GrantRevokeCreditsModal
        open={modal !== null}
        mode={modal ?? 'grant'}
        userId={userId}
        currentCredits={e.remainingCredits}
        onClose={() => setModal(null)}
      />
    </div>
  );
}

function Field({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: 'ok' | 'warn';
}) {
  const valueClass =
    tone === 'ok' ? 'text-emerald-300' : tone === 'warn' ? 'text-amber-300' : 'text-on-surface';
  return (
    <div className="glass-panel rounded-xl p-3">
      <div className="text-[10px] uppercase tracking-widest font-bold text-on-surface-variant">
        {label}
      </div>
      <div className={`text-sm font-bold ${valueClass}`}>{value}</div>
    </div>
  );
}
